/**
 * List-price lookup for ecommerce analytics events (#767): AddToCart / InitiateCheckout used to
 * send value:0 because the price was "determined by the backend". Value is the pre-discount
 * list price for the quantity; null when it cannot be resolved (callers then omit value).
 *
 * Follows Chargebee's `pricing_model` (verified on the item prices, see PR):
 *  - flat_fee  : fixed price regardless of quantity
 *  - per_unit  : price x quantity
 *  - volume    : the ONE tier containing the quantity prices ALL units
 *  - tiered    : graduated, each tier prices only the units that fall inside it
 * Anything else (stairstep, package, unknown) resolves to null rather than a guess.
 */

export interface CatalogueTier {
  starting_unit: number;
  ending_unit: number | null;
  /** cents (raw Chargebee) */
  price: number;
}

/** Entry of `chargebee.getItemPrices`; a flat `price` is already converted to dollars by the backend. */
export interface CatalogueItemPrice {
  id: string;
  pricing_model: string;
  /** dollars, flat_fee / per_unit only */
  price?: number;
  currency_code?: string;
  tiers?: CatalogueTier[];
}

export interface ListPrice {
  value: number;
  currency: string;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

export function isCatalogueTier(v: unknown): v is CatalogueTier {
  return isRecord(v)
    && typeof v.starting_unit === 'number'
    && (v.ending_unit === null || v.ending_unit === undefined || typeof v.ending_unit === 'number')
    && typeof v.price === 'number';
}

export function isCatalogueItemPrice(v: unknown): v is CatalogueItemPrice {
  return isRecord(v)
    && typeof v.id === 'string'
    && typeof v.pricing_model === 'string'
    && (v.price === undefined || v.price === null || typeof v.price === 'number')
    && (v.currency_code === undefined || typeof v.currency_code === 'string')
    && (v.tiers === undefined || v.tiers === null || (Array.isArray(v.tiers) && v.tiers.every(isCatalogueTier)));
}

/** Narrow an untyped API payload to the entries we can price; malformed entries are dropped. */
export function parseCatalogue(payload: unknown): CatalogueItemPrice[] {
  return Array.isArray(payload) ? payload.filter(isCatalogueItemPrice) : [];
}

const tierCovers = (t: CatalogueTier, q: number) => q >= t.starting_unit && (!t.ending_unit || q <= t.ending_unit);

function totalDollars(item: CatalogueItemPrice, quantity: number): number | undefined {
  const tiers = item.tiers ?? [];
  switch (item.pricing_model) {
    case 'flat_fee':
      return typeof item.price === 'number' ? item.price : undefined;
    case 'per_unit':
      return typeof item.price === 'number' ? item.price * quantity : undefined;
    case 'volume': {
      const tier = tiers.find((t) => tierCovers(t, quantity));
      return tier ? (tier.price / 100) * quantity : undefined;
    }
    case 'tiered': {
      let cents = 0;
      let covered = 0;
      for (const t of tiers) {
        if (quantity < t.starting_unit) continue;
        const upTo = t.ending_unit ? Math.min(t.ending_unit, quantity) : quantity;
        const units = upTo - t.starting_unit + 1;
        cents += units * t.price;
        covered += units;
      }
      return covered === quantity ? cents / 100 : undefined;
    }
    default:
      return undefined;
  }
}

export function resolveListPrice(
  itemPrices: readonly CatalogueItemPrice[] | undefined | null,
  itemPriceId: string,
  quantity: number,
): ListPrice | null {
  const item = itemPrices?.find((p) => p.id === itemPriceId);
  if (!item || !Number.isInteger(quantity) || quantity < 1) return null;

  const total = totalDollars(item, quantity);
  if (total === undefined || !(total > 0)) return null;

  return { value: Number(total.toFixed(2)), currency: item.currency_code || itemPriceId.split('-')[1] || 'USD' };
}
